import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import {
  Box,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Chip,
} from '@mui/material';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import GroupIcon from '@mui/icons-material/Group';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ListIcon from '@mui/icons-material/List';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import Button from '@mui/material/Button';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import UserNode from './hierarchy/UserNode';
import ReparentDialog from './hierarchy/ReparentDialog';
import ChangeTeamDialog from './hierarchy/ChangeTeamDialog';
import ChangeUplineDialog from './hierarchy/ChangeUplineDialog';
import HierarchyTreeView from './hierarchy/HierarchyTreeView';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 100;

const nodeTypes = { userNode: UserNode };

function getLayoutedElements(users) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 80, marginx: 40, marginy: 40 });

  const userMap = new Map();
  users.forEach((u) => userMap.set(u.iboNumber, u));

  const nodes = [];
  const edges = [];

  users.forEach((user) => {
    const nodeId = user.iboNumber;
    g.setNode(nodeId, { width: NODE_WIDTH, height: NODE_HEIGHT });
    nodes.push({
      id: nodeId,
      type: 'userNode',
      data: { user },
      position: { x: 0, y: 0 },
    });

    if (user.uplinePlatinumIbo && userMap.has(user.uplinePlatinumIbo)) {
      const edgeId = `${user.uplinePlatinumIbo}->${nodeId}`;
      g.setEdge(user.uplinePlatinumIbo, nodeId);
      edges.push({
        id: edgeId,
        source: user.uplinePlatinumIbo,
        target: nodeId,
        type: 'default',
        markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
        style: { stroke: '#667eea', strokeWidth: 1.5 },
        reconnectable: 'source',
      });
    }
  });

  dagre.layout(g);

  nodes.forEach((node) => {
    const pos = g.node(node.id);
    if (pos) {
      node.position = { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 };
    }
  });

  return { nodes, edges };
}

function HierarchyFlow() {
  const { getAccessToken, hasFeature } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextUser, setContextUser] = useState(null);
  const [reparentDialog, setReparentDialog] = useState({
    open: false, child: null, oldParent: null, newParent: null,
  });
  const [reparentLoading, setReparentLoading] = useState(false);
  const [changeTeamDialog, setChangeTeamDialog] = useState({ open: false, user: null });
  const [changeTeamLoading, setChangeTeamLoading] = useState(false);
  const [changeUplineDialog, setChangeUplineDialog] = useState({ open: false, user: null });
  const [changeUplineLoading, setChangeUplineLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('graph');
  const [editMode, setEditMode] = useState(false);
  const edgeReconnectSuccessful = useRef(true);
  const draggedNodeRef = useRef(null);
  const { fitView, setCenter, getIntersectingNodes } = useReactFlow();

  const userMap = useMemo(() => {
    const map = new Map();
    users.forEach((u) => map.set(u.iboNumber, u));
    return map;
  }, [users]);

  const teams = useMemo(() => {
    const set = new Set();
    users.forEach((u) => { if (u.teamName) set.add(u.teamName); });
    return Array.from(set).sort();
  }, [users]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Authentication token not available');

      const params = new URLSearchParams();
      if (teamFilter) params.set('teamName', teamFilter);

      const url = `${config.API_BASE_URL}/um/v1/admin/user-hierarchy/all-users${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const result = await response.json();
      const data = result.data || result;
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, teamFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (users.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const { nodes: ln, edges: le } = getLayoutedElements(users);
    ln.forEach((n) => {
      n.data = { ...n.data, onContextMenu: handleNodeContextMenu };
    });
    setNodes(ln);
    setEdges(le);
    setTimeout(() => fitView({ padding: 0.15 }), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const handleNodeContextMenu = useCallback((event, user) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
    setContextUser(user);
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
    setContextUser(null);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (!search) return;
    const s = search.toLowerCase();
    const found = nodes.find(
      (n) =>
        n.data.user.iboNumber?.toLowerCase().includes(s) ||
        n.data.user.iboName?.toLowerCase().includes(s) ||
        n.data.user.email?.toLowerCase().includes(s)
    );
    if (found) {
      setCenter(
        found.position.x + NODE_WIDTH / 2,
        found.position.y + NODE_HEIGHT / 2,
        { zoom: 1.2, duration: 600 }
      );
      setNodes((nds) =>
        nds.map((n) => ({ ...n, selected: n.id === found.id }))
      );
    }
  }, [search, nodes, setCenter, setNodes]);

  // Node drag-and-drop: drop a node onto another to reparent (edit mode only)
  const onNodeDragStart = useCallback((_, node) => {
    if (!editMode) return;
    draggedNodeRef.current = node;
  }, [editMode]);

  const onNodeDragStop = useCallback(
    (_, draggedNode) => {
      if (!editMode) return;
      const intersecting = getIntersectingNodes(draggedNode);
      const targetNode = intersecting.find((n) => n.id !== draggedNode.id);

      if (targetNode) {
        const child = userMap.get(draggedNode.id);
        const newParent = userMap.get(targetNode.id);
        if (child && newParent && child.iboNumber !== newParent.iboNumber) {
          const oldParent = child.uplinePlatinumIbo
            ? userMap.get(child.uplinePlatinumIbo)
            : null;
          setReparentDialog({ open: true, child, oldParent: oldParent || null, newParent });
        }
      }

      draggedNodeRef.current = null;
    },
    [editMode, getIntersectingNodes, userMap]
  );

  // Edge reconnection handlers (drag edge source to reparent, edit mode only)
  const onReconnectStart = useCallback(() => {
    if (!editMode) return;
    edgeReconnectSuccessful.current = false;
  }, [editMode]);

  const onReconnect = useCallback(
    (oldEdge, newConnection) => {
      if (!editMode) return;
      edgeReconnectSuccessful.current = true;
      const childIbo = oldEdge.target;
      const newParentIbo = newConnection.source;
      const oldParentIbo = oldEdge.source;

      if (newParentIbo === childIbo) return;

      const child = userMap.get(childIbo);
      const newParent = userMap.get(newParentIbo);
      const oldParent = userMap.get(oldParentIbo);

      if (!child || !newParent) return;

      setReparentDialog({ open: true, child, oldParent: oldParent || null, newParent });
    },
    [editMode, userMap]
  );

  const onReconnectEnd = useCallback(
    (_, edge) => {
      if (!editMode) return;
      if (!edgeReconnectSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      }
      edgeReconnectSuccessful.current = true;
    },
    [editMode, setEdges]
  );

  const handleReparentConfirm = useCallback(
    async ({ userId, newUplinePlatinumIbo, teamName }) => {
      setReparentLoading(true);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');

        const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user-hierarchy/reparent`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, newUplinePlatinumIbo, teamName }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || `Failed: ${response.status}`);
        }

        setSnackbar({ open: true, message: 'User reparented successfully', severity: 'success' });
        setReparentDialog({ open: false, child: null, oldParent: null, newParent: null });
        await fetchUsers();
      } catch (err) {
        setSnackbar({ open: true, message: err.message || 'Failed to reparent user', severity: 'error' });
      } finally {
        setReparentLoading(false);
      }
    },
    [getAccessToken, fetchUsers]
  );

  const handleClearOverride = useCallback(
    async (user) => {
      handleCloseContextMenu();
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');

        const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user-hierarchy/clear-override`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: user.id }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || `Failed: ${response.status}`);
        }

        setSnackbar({ open: true, message: `Override cleared for ${user.iboName || user.iboNumber}`, severity: 'success' });
        await fetchUsers();
      } catch (err) {
        setSnackbar({ open: true, message: err.message || 'Failed to clear override', severity: 'error' });
      }
    },
    [handleCloseContextMenu, getAccessToken, fetchUsers]
  );

  const handleViewProfile = useCallback(
    (user) => {
      handleCloseContextMenu();
      const node = nodes.find((n) => n.id === user.iboNumber);
      if (node) {
        setCenter(
          node.position.x + NODE_WIDTH / 2,
          node.position.y + NODE_HEIGHT / 2,
          { zoom: 1.5, duration: 600 }
        );
      }
    },
    [handleCloseContextMenu, nodes, setCenter]
  );

  const handleChangeUpline = useCallback(
    (user) => {
      handleCloseContextMenu();
      setChangeUplineDialog({ open: true, user });
    },
    [handleCloseContextMenu]
  );

  const handleChangeUplineConfirm = useCallback(
    async ({ userId, newUplinePlatinumIbo }) => {
      setChangeUplineLoading(true);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');

        const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user-hierarchy/reparent`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, newUplinePlatinumIbo }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || `Failed: ${response.status}`);
        }

        setSnackbar({ open: true, message: 'Upline changed successfully', severity: 'success' });
        setChangeUplineDialog({ open: false, user: null });
        await fetchUsers();
      } catch (err) {
        setSnackbar({ open: true, message: err.message || 'Failed to change upline', severity: 'error' });
      } finally {
        setChangeUplineLoading(false);
      }
    },
    [getAccessToken, fetchUsers]
  );

  const handleChangeTeam = useCallback(
    (user) => {
      handleCloseContextMenu();
      setChangeTeamDialog({ open: true, user });
    },
    [handleCloseContextMenu]
  );

  const handleChangeTeamConfirm = useCallback(
    async ({ userId, teamName }) => {
      setChangeTeamLoading(true);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');

        const response = await fetch(`${config.API_BASE_URL}/um/v1/admin/user-hierarchy/change-team`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, teamName }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || `Failed: ${response.status}`);
        }

        setSnackbar({ open: true, message: 'Team changed successfully', severity: 'success' });
        setChangeTeamDialog({ open: false, user: null });
        await fetchUsers();
      } catch (err) {
        setSnackbar({ open: true, message: err.message || 'Failed to change team', severity: 'error' });
      } finally {
        setChangeTeamLoading(false);
      }
    },
    [getAccessToken, fetchUsers]
  );

  if (!hasFeature('USER_HIERARCHY')) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error">Access denied. You do not have the User Hierarchy feature enabled.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <IconButton onClick={fetchUsers}><RefreshIcon /></IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mr: 1, whiteSpace: 'nowrap' }}>
          User Hierarchy
        </Typography>

        <TextField
          size="small"
          placeholder="Search by IBO, name, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSearchFocus(); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260 }}
        />

        <Tooltip title="Find & center on user" arrow>
          <IconButton size="small" onClick={handleSearchFocus}>
            <CenterFocusStrongIcon />
          </IconButton>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Team Filter</InputLabel>
          <Select
            value={teamFilter}
            label="Team Filter"
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <MenuItem value="">All Teams</MenuItem>
            {teams.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Refresh data" arrow>
          <IconButton size="small" onClick={fetchUsers}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Fit view" arrow>
          <IconButton size="small" onClick={() => fitView({ padding: 0.15, duration: 400 })}>
            <FitScreenIcon />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        <Chip label={`${users.length} users`} size="small" variant="outlined" />

        <Button
          variant={editMode ? 'contained' : 'outlined'}
          size="small"
          startIcon={editMode ? <EditIcon /> : <LockIcon />}
          onClick={() => setEditMode((prev) => !prev)}
          color={editMode ? 'warning' : 'inherit'}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {editMode ? 'Editing' : 'View Only'}
        </Button>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          size="small"
          sx={{ ml: 1 }}
        >
          <ToggleButton value="graph" aria-label="Graph view">
            <Tooltip title="Graph View" arrow><AccountTreeIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="tree" aria-label="Tree view">
            <Tooltip title="Tree List View" arrow><ListIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* View area */}
      {viewMode === 'graph' ? (
        <Box sx={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={editMode ? onNodeDragStart : undefined}
            onNodeDragStop={editMode ? onNodeDragStop : undefined}
            onReconnect={editMode ? onReconnect : undefined}
            onReconnectStart={editMode ? onReconnectStart : undefined}
            onReconnectEnd={editMode ? onReconnectEnd : undefined}
            nodesDraggable={editMode}
            edgesReconnectable={editMode}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'default',
              markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
              style: { stroke: '#667eea', strokeWidth: 1.5 },
            }}
            proOptions={{ hideAttribution: true }}
          >
            <Controls position="bottom-left" />
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              position="bottom-right"
              style={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)' }}
            />
            <Background variant="dots" gap={16} size={1} />
          </ReactFlow>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <HierarchyTreeView
            users={users}
            editMode={editMode}
            onContextMenu={(event, user) => {
              event.preventDefault();
              setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
              setContextUser(user);
            }}
            onDropReparent={editMode ? (childUser, newParentUser) => {
              const oldParent = childUser.uplinePlatinumIbo
                ? userMap.get(childUser.uplinePlatinumIbo)
                : null;
              setReparentDialog({ open: true, child: childUser, oldParent: oldParent || null, newParent: newParentUser });
            } : undefined}
          />
        </Box>
      )}

      {/* Context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextUser && [
          ...(viewMode === 'graph'
            ? [
                <MenuItem key="view" onClick={() => handleViewProfile(contextUser)}>
                  <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Focus on User</ListItemText>
                </MenuItem>,
              ]
            : []),
          ...(editMode
            ? [
                <MenuItem key="reparent" onClick={() => handleChangeUpline(contextUser)}>
                  <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Change Upline</ListItemText>
                </MenuItem>,
                <MenuItem key="team" onClick={() => handleChangeTeam(contextUser)}>
                  <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Change Team</ListItemText>
                </MenuItem>,
                ...(contextUser.uplineOverride
                  ? [
                      <MenuItem key="clear" onClick={() => handleClearOverride(contextUser)}>
                        <ListItemIcon><LinkOffIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Clear Override</ListItemText>
                      </MenuItem>,
                    ]
                  : []),
              ]
            : [
                <MenuItem key="readonly-hint" disabled>
                  <ListItemText sx={{ fontStyle: 'italic' }}>Enable editing to modify</ListItemText>
                </MenuItem>,
              ]),
        ]}
      </Menu>

      {/* Reparent dialog (drag-and-drop) */}
      <ReparentDialog
        open={reparentDialog.open}
        onClose={() => setReparentDialog({ open: false, child: null, oldParent: null, newParent: null })}
        onConfirm={handleReparentConfirm}
        childUser={reparentDialog.child}
        oldParent={reparentDialog.oldParent}
        newParent={reparentDialog.newParent}
        loading={reparentLoading}
      />

      {/* Change Team dialog */}
      <ChangeTeamDialog
        open={changeTeamDialog.open}
        onClose={() => setChangeTeamDialog({ open: false, user: null })}
        onConfirm={handleChangeTeamConfirm}
        user={changeTeamDialog.user}
        loading={changeTeamLoading}
      />

      {/* Change Upline dialog */}
      <ChangeUplineDialog
        open={changeUplineDialog.open}
        onClose={() => setChangeUplineDialog({ open: false, user: null })}
        onConfirm={handleChangeUplineConfirm}
        user={changeUplineDialog.user}
        users={users}
        loading={changeUplineLoading}
      />

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function UserHierarchyPage() {
  return (
    <ReactFlowProvider>
      <HierarchyFlow />
    </ReactFlowProvider>
  );
}
